import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbModal from '../security-toolbox-ui/StbModal';
import { useLabScenarios, useLabSessions, useStartLabRun } from '../../hooks/lab-hooks';
import { LabRun } from '../../support/labService';
import { labErrorMessage } from './LabShared';

export type LabStartRunModalProps = {
  open: boolean;
  /** Fixed when opened from inside a session. */
  sessionKey?: string;
  onClose: () => void;
  onStarted?: (run: LabRun) => void;
};

/**
 * Open a run against a session.
 *
 * Only sessions the caller can reach are offered, and the backend checks again - a session
 * you are not a participant of is a 403, and one that is not active is a 409. Starting a run
 * for somebody else is allowed (a proctor setting up a pod), which is why the email field is
 * here and why leaving it empty means "for me".
 */
const LabStartRunModal: React.FC<LabStartRunModalProps> = ({
  open,
  sessionKey,
  onClose,
  onStarted,
}) => {
  const [session, setSession] = useState(sessionKey || '');
  const [scenarioSlug, setScenarioSlug] = useState('');
  const [forUserEmail, setForUserEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const { data: sessions } = useLabSessions(true, { enabled: open && !sessionKey });
  const { data: scenarios } = useLabScenarios({}, { enabled: open });
  const start = useStartLabRun();

  useEffect(() => {
    if (!open) return;

    setSession(sessionKey || '');
    setScenarioSlug('');
    setForUserEmail('');
    setFormError(null);
    start.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionKey]);

  const handleSubmit = async () => {
    if (!session) {
      setFormError('Choose a session.');
      return;
    }

    if (!scenarioSlug) {
      setFormError('Choose a scenario.');
      return;
    }

    setFormError(null);

    try {
      const run = await start.mutateAsync({
        session_key: session,
        scenario: scenarioSlug,
        ...(forUserEmail.trim() ? { for_user_email: forUserEmail.trim() } : {}),
      });
      onStarted?.(run);
      onClose();
    } catch {
      // Surfaced through start.error.
    }
  };

  const submitError = formError || (start.isError ? labErrorMessage(start.error) : null);

  return (
    <StbModal
      open={open}
      role="update"
      title="Start a lab run"
      submitLabel="Start run"
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? <p className="m-0 text-sm text-amber-800">{submitError}</p> : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {sessionKey ? (
          <div className="text-sm text-gray-600">{`Session ${sessionKey}`}</div>
        ) : (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-gray-700">Session</span>
            <select
              className="rounded border border-gray-300 p-2 text-sm"
              value={session}
              onChange={(evt) => setSession(evt.target.value)}
            >
              <option value="">Choose an active session</option>
              {(sessions || [])
                .filter((candidate) => candidate.is_active)
                .map((candidate) => (
                  <option value={candidate.session_key} key={candidate.session_key}>
                    {`${candidate.name || candidate.demo_name || candidate.session_key} (${candidate.session_key})`}
                  </option>
                ))}
            </select>
            <span className="text-xs text-gray-500">
              Only active sessions can take a run. A scheduled one is refused until it starts.
            </span>
          </label>
        )}

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-gray-700">Scenario</span>
          <select
            className="rounded border border-gray-300 p-2 text-sm"
            value={scenarioSlug}
            onChange={(evt) => setScenarioSlug(evt.target.value)}
          >
            <option value="">Choose a scenario</option>
            {(scenarios || [])
              .filter((scenario) => scenario.enabled)
              .map((scenario) => (
                <option value={scenario.slug} key={scenario.slug}>
                  {scenario.name}
                </option>
              ))}
          </select>
        </label>

        <TextField
          label="Run it for (optional)"
          value={forUserEmail}
          onChange={(evt) => setForUserEmail(evt.target.value)}
          helperText="Leave empty to open the run for yourself."
          fullWidth
        />
      </div>
    </StbModal>
  );
};

export default LabStartRunModal;
